// src/pages/Resources/SitePermissionsModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Table, Spinner } from 'react-bootstrap';
import { FaUsers, FaSearch, FaExternalLinkAlt } from 'react-icons/fa';

interface SitePermission {
  userId: string;
  displayName: string;
  email: string;
  permissionLevel: string;
  grantedThrough: string; // "Direct", "Group", "Sharing Link"
  groupName?: string;
  photoUrl?: string;
}

interface Props {
  show: boolean;
  groupName: string;
  internalResourceId: string;
  onClose: () => void;
  onUserClick: (userId: string) => void;
}

const SitePermissionsModal: React.FC<Props> = ({
  show,
  groupName,
  internalResourceId,
  onClose,
  onUserClick
}) => {
  const [permissions, setPermissions] = useState<SitePermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [permissionFilter, setPermissionFilter] = useState<string>('All');

  useEffect(() => {
    if (show && internalResourceId) {
      loadSitePermissions();
    }
  }, [show, internalResourceId]);

  const loadSitePermissions = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/resources/${internalResourceId}/site-permissions`,
        {
          headers: {
            Authorization: `Bearer ${await acquireToken()}`,
          },
        }
      );
      
      if (!response.ok) throw new Error('Failed to load site permissions');
      
      const data = await response.json();
      setPermissions(data);
    } catch (error) {
      console.error('Error loading site permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const acquireToken = async () => {
    // Placeholder - should come from context/parent
    return 'token';
  };

  const filteredPermissions = permissions.filter(p => {
    const matchesSearch = !filter || 
      p.displayName.toLowerCase().includes(filter.toLowerCase()) ||
      p.email.toLowerCase().includes(filter.toLowerCase());
    
    const matchesPermission = permissionFilter === 'All' || 
      p.permissionLevel === permissionFilter;
    
    return matchesSearch && matchesPermission;
  });

  const permissionLevels = ['All', ...new Set(permissions.map(p => p.permissionLevel))];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" className="site-permissions-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUsers className="me-2" />
          Site Permissions - {groupName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="alert alert-info small mb-3">
          <strong>All Users with Site Access</strong>
          <p className="mb-0 mt-1">
            This shows everyone who has access to the SharePoint site, including users
            with direct permissions, group members, and those with sharing links.
          </p>
        </div>

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-8">
            <Form.Group>
              <Form.Label className="small fw-semibold">Search Users</Form.Label>
              <div className="input-group">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search by name or email..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group>
              <Form.Label className="small fw-semibold">Permission Level</Form.Label>
              <Form.Select
                value={permissionFilter}
                onChange={(e) => setPermissionFilter(e.target.value)}
              >
                {permissionLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-muted">Loading permissions...</div>
          </div>
        ) : (
          <>
            <div className="mb-2 text-muted small">
              Showing {filteredPermissions.length} of {permissions.length} users
            </div>
            
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table hover size="sm">
                <thead className="sticky-top permissions-table-head">
                  <tr>
                    <th style={{ width: '40%' }}>User</th>
                    <th style={{ width: '20%' }}>Permission</th>
                    <th style={{ width: '30%' }}>Granted Through</th>
                    <th style={{ width: '10%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPermissions.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredPermissions.map((perm) => (
                      <tr key={perm.userId}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="member-avatar">
                              {perm.photoUrl ? (
                                <img
                                  src={perm.photoUrl}
                                  alt={perm.displayName}
                                  className="member-avatar-img"
                                  style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                                />
                              ) : (
                                <div 
                                  className="member-avatar-initials"
                                  style={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    borderRadius: '50%',
                                    background: 'var(--accent-bg)',
                                    color: 'var(--accent-fg)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {getInitials(perm.displayName)}
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="fw-semibold">{perm.displayName}</div>
                              <div className="small text-muted">{perm.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-primary">{perm.permissionLevel}</span>
                        </td>
                        <td>
                          <div>
                            {perm.grantedThrough}
                            {perm.groupName && (
                              <div className="small text-muted">via {perm.groupName}</div>
                            )}
                          </div>
                        </td>
                        <td className="text-end">
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => onUserClick(perm.userId)}
                            title="View user details"
                          >
                            <FaExternalLinkAlt />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SitePermissionsModal;
